export const PENDING_PLANNING_DRAFT_KEY = 'odyssey-pending-planning-draft';

// Optional storage must not block navigation. A same-page value also prevents
// a failed write/remove from replaying an older persisted draft during this run.
let currentPageDraft;
let currentOwnerId = null;

const readRecord = () => {
  if (currentPageDraft !== undefined) return currentPageDraft;
  try {
    const record = JSON.parse(window.sessionStorage.getItem(PENDING_PLANNING_DRAFT_KEY));
    if (record?.version === 2 && typeof record.draft === 'string'
      && (record.ownerId === null || (typeof record.ownerId === 'string' && record.ownerId))) {
      return record;
    }
  } catch {
    // Legacy unowned drafts and unavailable storage cannot establish ownership.
  }
  return null;
};

const persistRecord = (record) => {
  currentPageDraft = record;
  try {
    window.sessionStorage.setItem(PENDING_PLANNING_DRAFT_KEY, JSON.stringify(record));
  } catch {
    // Browser policy or quota can disable even the sessionStorage getter.
  }
};

// The per-tab draft needs its own owner: another tab can replace trip-storage
// while this document is away. Run before authenticated children are rendered.
export const reconcilePlanningDraftOwner = (userId) => {
  currentOwnerId = typeof userId === 'string' && userId ? userId : null;
  if (typeof window === 'undefined') return false;
  const record = readRecord();
  if (!record) {
    clearPlanningDraft();
    return false;
  }
  if (record.ownerId !== null && record.ownerId !== currentOwnerId) {
    clearPlanningDraft();
    return true;
  }
  // Genuine guest drafts are promoted on sign-in, never left unowned afterward.
  persistRecord({ ...record, ownerId: currentOwnerId });
  return false;
};

export const savePlanningDraft = (value) => {
  if (typeof window === 'undefined') return;
  const draft = String(value || '').trim();
  if (draft) {
    persistRecord({ version: 2, ownerId: currentOwnerId, draft });
  }
};

export const readPlanningDraft = () => {
  if (typeof window === 'undefined') return '';
  const record = readRecord();
  return record && (record.ownerId === null || record.ownerId === currentOwnerId)
    ? record.draft : '';
};

// Unsent composer data shares the handoff's per-tab ownership boundary, but
// is restored for review only: readPlanningDraft never returns this content.
export const readComposerDraft = () => {
  if (typeof window === 'undefined') return null;
  const record = readRecord();
  if (!record || (record.ownerId !== null && record.ownerId !== currentOwnerId)) return null;
  const composer = record.composer;
  if (!composer || typeof composer.message !== 'string' || !composer.fields) return null;
  const fields = Object.fromEntries(
    ['destination', 'startDate', 'endDate', 'budget', 'travelers'].map(key => [
      key, typeof composer.fields[key] === 'string' ? composer.fields[key] : '',
    ]),
  );
  return { message: composer.message, fields };
};

export const saveComposerDraft = (message, fields) => {
  if (typeof window === 'undefined') return;
  const record = readRecord();
  const sameOwner = record && (record.ownerId === null || record.ownerId === currentOwnerId);
  persistRecord({
    version: 2,
    ownerId: currentOwnerId,
    draft: sameOwner ? record.draft : '',
    composer: { message, fields },
  });
};

export const clearPlanningDraft = () => {
  if (typeof window === 'undefined') return;
  currentPageDraft = null;
  try {
    window.sessionStorage.removeItem(PENDING_PLANNING_DRAFT_KEY);
  } catch {
    // The in-page clear remains authoritative until this document is replaced.
  }
};
